import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, MapPin } from 'lucide-react';
import Button from 'components/crm/ui/Button';
import Chip from 'components/crm/ui/Chip';
import CrmScope from 'components/crm/ui/CrmScope';
import Drawer from 'components/crm/ui/Drawer';
import RichSelect from 'components/crm/ui/RichSelect';
import Switch from 'components/crm/ui/Switch';
import TextField from 'components/crm/ui/TextField';
import { useToast } from 'components/crm/ui/Toast';
import CompanyDirectorySearch from 'components/prospection/CompanyDirectorySearch';
import FollowedCompany, {
  CompanyCard,
} from 'components/prospection/FollowedCompany';
import PhoneField from 'components/prospection/PhoneField';
import ProspectContacts from 'components/prospection/ProspectContacts';
import {
  buildProspectPayload,
  emptyProspectForm,
  isProspectFormValid,
} from 'components/prospection/prospectForm';
import { PROSPECT_SECTORS, PROSPECT_SOURCES } from 'helper/prospectOptions';
import {
  isCanceledRequest,
  prospectionErrorText,
} from 'helper/prospectionErrors';
import UseContext from 'hooks/useContext';
import useText from 'hooks/useText';
import { createProspect } from 'server/crm/prospectionApi';

// Mounted only while open, so that every opening starts from an empty form.
const AddProspectPanel = ({ onClose, onAdded }) => {
  const { text: prospectionText } = useText('prospection');
  const { text } = useText('prospection.addProspect');
  const { userData } = UseContext();
  const toast = useToast();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [form, setForm] = useState(emptyProspectForm);
  const [nameLeft, setNameLeft] = useState(false);
  const [busy, setBusy] = useState(false);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const pick = (picked) => {
    setCompany(picked);
    setNameLeft(false);
    setForm((f) => ({
      ...f,
      name: picked.name ?? '',
      address: picked.manual ? '' : (picked.address ?? ''),
    }));
  };

  const change = () => setCompany(null);

  const valid = isProspectFormValid({ company, form });
  const followed = company?.followed ?? null;

  const submit = () => {
    if (!valid || busy) return;
    setBusy(true);
    createProspect(buildProspectPayload({ company, form })).then(
      (result) => {
        toast(text('added'));
        onAdded?.(result);
        onClose();
      },
      (failure) => {
        setBusy(false);
        if (isCanceledRequest(failure)) return;
        toast(
          text('failed', {
            error: prospectionErrorText(prospectionText, failure),
          }),
          'error',
        );
      },
    );
  };

  const sectorOptions = PROSPECT_SECTORS.map((code) => ({
    value: code,
    label: text(`sectors.${code}`),
  }));

  const renderForm = () => (
    <>
      {company.manual ? (
        <TextField
          id="prospect-name"
          label={text('company.nameLabel')}
          icon={Building2}
          value={form.name}
          onChange={(event) => update('name', event.target.value)}
          onBlur={() => setNameLeft(true)}
          error={
            nameLeft && !form.name.trim()
              ? text('company.nameRequired')
              : undefined
          }
        />
      ) : (
        <CompanyCard company={company} onChange={change} />
      )}
      <TextField
        id="prospect-address"
        label={text('address.label')}
        icon={MapPin}
        value={form.address}
        helper={company.manual ? undefined : text('address.hint')}
        onChange={(event) => update('address', event.target.value)}
      />
      <PhoneField
        id="prospect-phone"
        label={text('phone.label')}
        placeholder={text('phone.placeholder')}
        value={form.phone}
        required
        onChange={(value) => update('phone', value)}
      />
      <ProspectContacts
        contacts={form.contacts}
        onChange={(contacts) => update('contacts', contacts)}
      />
      <RichSelect
        id="prospect-sector"
        label={text('sector.label')}
        placeholder={text('sector.placeholder')}
        hint={text('sector.hint')}
        value={form.sector}
        options={sectorOptions}
        onChange={(value) => update('sector', value)}
      />
      <div className="flex flex-col gap-3">
        <p className="text-sm font-semibold text-ink">{text('source.label')}</p>
        <div className="flex flex-wrap gap-2">
          {PROSPECT_SOURCES.map((code) => (
            <Chip
              key={code}
              selected={form.source === code}
              onClick={() => update('source', code)}
            >
              {prospectionText(`sources.${code}`)}
            </Chip>
          ))}
        </div>
      </div>
      <Switch
        checked={form.today}
        onChange={(today) => update('today', today)}
        label={text('today')}
      />
    </>
  );

  const renderBody = () => {
    if (!company) {
      return <CompanyDirectorySearch onPick={pick} autoFocus />;
    }
    if (followed) {
      return (
        <FollowedCompany
          company={company}
          currentUserId={userData?._id}
          onChange={change}
        />
      );
    }
    return renderForm();
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={text('title')}
      description={<CrmScope>{text('subtitle')}</CrmScope>}
      footer={
        <CrmScope className="flex flex-1 items-center justify-end gap-3">
          <Button variant="secondary" onClick={onClose}>
            {text('cancel')}
          </Button>
          {followed ? (
            <Button onClick={() => navigate(`/company/${followed.companyId}`)}>
              {text('followed.open')}
            </Button>
          ) : (
            <Button disabled={!valid || busy} onClick={submit}>
              {busy ? text('submitting') : text('submit')}
            </Button>
          )}
        </CrmScope>
      }
    >
      <CrmScope className="flex flex-col gap-6">{renderBody()}</CrmScope>
    </Drawer>
  );
};

const AddProspectDrawer = ({ open, onClose, onAdded }) =>
  open ? <AddProspectPanel onClose={onClose} onAdded={onAdded} /> : null;

export default AddProspectDrawer;
